import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (
      this.closest("product-card")?.querySelector('a[ref="productCardLink"]')
    )?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        "text/html",
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute("stay-visible", true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute("stay-visible", false),
      {
        once: true,
      },
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById("quick-add-dialog");
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector("[data-product-grid-content]");
    const modalContent = document.getElementById("quick-add-modal-content");

    if (!productGrid || !modalContent) return;

    // if (isMobileBreakpoint()) {
    const productDetails = productGrid.querySelector(".product-details");
    if (!productDetails) return;
    const productFormComponent = productGrid.querySelector(
      "product-form-component",
    );
    const variantPicker = productGrid.querySelector("variant-picker");
    const variantForms = productGrid.querySelectorAll(".variant-picker__form");
    const customVariantsJson = productGrid.querySelector(
      "[data-custom-variants-json]",
    );
    const productPrice = productGrid.querySelector("product-price");
    
    const viewProductTitle = productDetails.querySelector(".view-product-title");
    const productDescription = productDetails.querySelector(".collapse_text") || productDetails.querySelector(".text-block.rte:not(product-price)");

    if (
      !productFormComponent ||
      !variantPicker ||
      !productPrice
    )
      return;

    const productInventory = productGrid.querySelector("product-inventory");
    const hasCustomVariants = productGrid.querySelector(
      "[data-custom-variants]",
    );

    if (hasCustomVariants) {
      variantPicker.style.setProperty("display", "none", "important");
    }

    const productHeader = document.createElement("div");
    productHeader.classList.add("product-header");

    if (viewProductTitle) {
      productHeader.appendChild(viewProductTitle);
    } else {
      const productTitle = document.createElement("a");
      productTitle.textContent = this.dataset.productTitle || "";
      productTitle.href = this.productPageUrl;
      productHeader.appendChild(productTitle);
    }

    productGrid.appendChild(productHeader);
    if (productDescription) {
      productGrid.appendChild(productDescription);
    }
    productGrid.appendChild(variantPicker);
    if (hasCustomVariants) productGrid.appendChild(hasCustomVariants);
    if (customVariantsJson) productGrid.appendChild(customVariantsJson);
    productGrid.appendChild(productPrice);
    if (productInventory) productGrid.appendChild(productInventory);
    productGrid.appendChild(productFormComponent);
    productDetails.remove();
    // }

    morph(modalContent, productGrid);

    // Copy data-product-template to the modal wrapper so applyStockUI can read it
    const productTemplate = productGrid.dataset.productTemplate || "";
    if (productTemplate) modalContent.dataset.productTemplate = productTemplate;
    else delete modalContent.dataset.productTemplate;

    // After morphing, we need to initialize the variant sync with the new DOM elements
    if (window.theme && typeof theme.initCustomVariantSync === "function") {
      setTimeout(() => {
        // Reset the custom UI guard so it can re-init in modal context
        const customContainer = modalContent.querySelector(
          "[data-custom-variants]",
        );
        if (customContainer) {
          delete customContainer.dataset.customUiInitialized;
          if (typeof window.initCustomVariantUI === "function") {
            window.initCustomVariantUI(customContainer);
          }
        }

        // Reset and re-init custom variant sync
        delete modalContent.dataset.customSyncInitialized;
        theme.initCustomVariantSync(modalContent, { skipInitialSync: true });

        // Trigger initial stock/price check for the modal variant
        const picker = modalContent.querySelector("variant-picker");
        if (
          picker &&
          window.theme &&
          typeof theme.updateStockDisplay === "function"
        ) {
          const opt =
            picker.querySelector(
              "select option[selected], fieldset input:checked",
            ) || picker.querySelector("select option");
          if (opt && opt.dataset.material) {
            theme.updateStockDisplay(opt.dataset.material, modalContent);
            if (
              opt.dataset.prmValue &&
              typeof theme.updatePriceDisplay === "function"
            ) {
              theme.updatePriceDisplay(
                opt.dataset.material,
                opt.dataset.prmValue,
                modalContent,
              );
            }
          }
        }
      }, 50);
    }
  }
}

if (!customElements.get("quick-add-component")) {
  customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#updateProductTitleLink,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector(".view-product-title a")
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".view-product-title a")
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector(".product-header a")
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get("quick-add-dialog")) {
  customElements.define("quick-add-dialog", QuickAddDialog);
}
